import { useMemo } from 'react';
import { Cpu, AlertTriangle } from 'lucide-react';
import { usePodMetrics, useClusterNodes } from './useClusterData';

interface Props {
  siteId: string;
}

/** Parse K8s CPU values to millicores */
function parseCpu(val: string): number {
  if (!val) return 0;
  if (val.endsWith('n')) return parseInt(val) / 1_000_000;
  if (val.endsWith('u')) return parseInt(val) / 1_000;
  if (val.endsWith('m')) return parseInt(val);
  return parseFloat(val) * 1000;
}

/** Parse K8s memory values to MiB */
function parseMemory(val: string): number {
  if (!val) return 0;
  if (val.endsWith('Ki')) return parseInt(val) / 1024;
  if (val.endsWith('Mi')) return parseInt(val);
  if (val.endsWith('Gi')) return parseInt(val) * 1024;
  if (val.endsWith('Ti')) return parseInt(val) * 1024 * 1024;
  if (val.endsWith('k')) return parseInt(val) / 1024;
  if (val.endsWith('M')) return parseInt(val);
  if (val.endsWith('G')) return parseInt(val) * 1024;
  return parseInt(val) / (1024 * 1024); // bytes
}

function formatCpu(millicores: number): string {
  if (millicores >= 1000) return `${(millicores / 1000).toFixed(1)} cores`;
  return `${Math.round(millicores)}m`;
}

function formatMemory(mib: number): string {
  if (mib >= 1024) return `${(mib / 1024).toFixed(1)} Gi`;
  return `${Math.round(mib)} Mi`;
}

function UsageBar({ used, total, label }: { used: number; total: number; label: string }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const color = pct > 90 ? 'bg-status-error' : pct > 70 ? 'bg-status-warning' : 'bg-status-healthy';

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-silver-muted">{label}</span>
        <span className="text-silver-bright font-mono">{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 bg-surface-elevated rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ResourcesPanel({ siteId }: Props) {
  const { data: podMetrics, isLoading: metricsLoading } = usePodMetrics(siteId);
  const { data: nodes, isLoading: nodesLoading } = useClusterNodes(siteId);

  // Aggregate pod metrics for "top pods" table
  const topPods = useMemo(() => {
    if (!podMetrics) return [];
    return podMetrics
      .map((pm) => {
        const cpuTotal = pm.containers.reduce((sum, c) => sum + parseCpu(c.cpu), 0);
        const memTotal = pm.containers.reduce((sum, c) => sum + parseMemory(c.memory), 0);
        return { name: pm.name, namespace: pm.namespace, cpu: cpuTotal, memory: memTotal };
      })
      .sort((a, b) => b.cpu - a.cpu)
      .slice(0, 20);
  }, [podMetrics]);

  // Node resource utilization
  const nodeUsage = useMemo(() => {
    if (!nodes) return [];
    return nodes.map((n) => ({
      name: n.name,
      cpuAllocatable: parseCpu(n.allocatable.cpu),
      cpuUsage: n.usage ? parseCpu(n.usage.cpu) : 0,
      memAllocatable: parseMemory(n.allocatable.memory),
      memUsage: n.usage ? parseMemory(n.usage.memory) : 0,
      diskCapacity: parseMemory(n.capacity.ephemeralStorage),
      diskAllocatable: parseMemory(n.allocatable.ephemeralStorage),
      gpuCapacity: parseInt(n.capacity.gpu || '0', 10),
      gpuAllocatable: parseInt(n.allocatable.gpu || '0', 10),
      hasMetrics: !!n.usage,
    }));
  }, [nodes]);

  // Node conditions (pressure)
  const pressureConditions = useMemo(() => {
    if (!nodes) return [];
    return nodes.flatMap((n) =>
      n.conditions
        .filter((c) => c.status === 'True' && ['DiskPressure', 'MemoryPressure', 'PIDPressure'].includes(c.type))
        .map((c) => ({ node: n.name, type: c.type, message: c.message ?? '' })),
    );
  }, [nodes]);

  // GPU info: prefer K8s capacity (nvidia.com/gpu), fall back to hardware DB data
  const gpuNodes = useMemo(() => {
    if (!nodes) return [];
    return nodes
      .filter((n) => parseInt(n.capacity.gpu || '0', 10) > 0 || n.hardware.gpus.length > 0)
      .map((n) => ({
        name: n.name,
        k8sGpus: parseInt(n.capacity.gpu || '0', 10),
        k8sAllocatable: parseInt(n.allocatable.gpu || '0', 10),
        hardwareGpus: n.hardware.gpus,
      }));
  }, [nodes]);
  const hasGpus = gpuNodes.length > 0;

  const isLoading = metricsLoading && nodesLoading;

  if (isLoading && !podMetrics && !nodes) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="p-8 space-y-3 animate-pulse">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 bg-surface-elevated rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pressure warnings */}
      {pressureConditions.length > 0 && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-status-error" />
            <h3 className="text-xs font-medium text-status-error uppercase tracking-wider">Node Pressure Detected</h3>
          </div>
          {pressureConditions.map((c, i) => (
            <div key={i} className="text-xs text-status-error">
              <span className="font-mono font-medium">{c.node}</span>: {c.type}{c.message ? ` — ${c.message}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Node utilization */}
      {nodeUsage.length > 0 && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-4">
          <h3 className="text-xs font-medium text-silver-muted uppercase tracking-wider mb-3">Node Utilization</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nodeUsage.map((n) => (
              <div key={n.name} className="space-y-2 p-3 bg-surface-deep rounded-lg">
                <h4 className="text-xs text-silver-bright font-mono">{n.name}</h4>
                {n.hasMetrics ? (
                  <>
                    <UsageBar
                      used={n.cpuUsage}
                      total={n.cpuAllocatable}
                      label={`CPU: ${formatCpu(n.cpuUsage)} / ${formatCpu(n.cpuAllocatable)}`}
                    />
                    <UsageBar
                      used={n.memUsage}
                      total={n.memAllocatable}
                      label={`Memory: ${formatMemory(n.memUsage)} / ${formatMemory(n.memAllocatable)}`}
                    />
                    {n.diskAllocatable > 0 && (
                      <div className="text-xs">
                        <span className="text-silver-muted">Disk: </span>
                        <span className="text-silver-bright font-mono">{formatMemory(n.diskAllocatable)}</span>
                      </div>
                    )}
                    {(n.gpuCapacity > 0 || (nodes?.find((nd) => nd.name === n.name)?.hardware.gpus.length ?? 0) > 0) && (
                      <div className="text-xs">
                        <span className="text-silver-muted">GPUs: </span>
                        <span className="text-silver-bright font-mono">
                          {n.gpuCapacity > 0 ? `${n.gpuAllocatable} / ${n.gpuCapacity}` : `${nodes?.find((nd) => nd.name === n.name)?.hardware.gpus.length ?? 0} (not in K8s)`}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-silver-muted">Metrics unavailable</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GPU summary */}
      {hasGpus && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-4">
          <h3 className="text-xs font-medium text-silver-muted uppercase tracking-wider mb-3">GPU Resources</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-elevated">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Node</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">GPU</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Count</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">K8s Allocatable</th>
                </tr>
              </thead>
              <tbody>
                {gpuNodes.map((n) => (
                  <tr key={n.name} className="border-b border-surface-elevated">
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{n.name}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-bright">
                      {n.hardwareGpus.length > 0
                        ? n.hardwareGpus.map((g) => `${g.name}${g.vram ? ` (${g.vram})` : ''}`).join(', ')
                        : `${n.k8sGpus} GPU(s)`}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{n.hardwareGpus.length || n.k8sGpus}</td>
                    <td className="px-4 py-2.5 text-xs font-mono">
                      {n.k8sAllocatable > 0
                        ? <span className="text-status-healthy">{n.k8sAllocatable}</span>
                        : <span className="text-status-warning">Not registered (install NVIDIA device plugin)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top pods by CPU */}
      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-elevated">
          <h3 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Top Pods by CPU</h3>
        </div>
        {topPods.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Cpu size={40} className="mx-auto text-silver-muted/30" />
            <p className="text-silver-muted text-sm">
              {metricsLoading ? 'Loading metrics...' : 'No pod metrics available (metrics-server may not be installed)'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-elevated">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Pod</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Namespace</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">CPU</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Memory</th>
                </tr>
              </thead>
              <tbody>
                {topPods.map((pod) => (
                  <tr key={`${pod.namespace}/${pod.name}`} className="border-b border-surface-elevated hover:bg-surface-elevated/30 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{pod.name}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-muted">{pod.namespace}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{formatCpu(pod.cpu)}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{formatMemory(pod.memory)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
